/**
 * PlanCard — W2.6
 *
 * Zeigt einen einzelnen Plan mit Name, Preis, Hotspot-Limit.
 * Kampagnen-Badge bei campaign_active: true.
 * Dauer-Selector aktualisiert den angezeigten Preis.
 * "Abonnieren"-Button öffnet CheckoutModal.
 */
import { useTranslation } from 'react-i18next'
import type { Plan } from './useSubscriptionPlans'
import { displayPrice } from './useSubscriptionPlans'
import type { DurationMonths } from './subscriptionUtils'
import { fmtPrice } from '../../lib/fmt'

type PlanCardProps = {
  plan: Plan
  duration: DurationMonths
  onSubscribe: (plan: Plan) => void
}

export function PlanCard({ plan, duration, onSubscribe }: PlanCardProps) {
  const { t } = useTranslation('subscription')
  const total = displayPrice(plan, duration)
  const perMonth = total / duration

  return (
    <div
      className="card"
      data-testid={`plan-card-${plan.id}`}
      style={{ position: 'relative', padding: '24px' }}
    >
      {plan.campaign_active && (
        <span
          className="badge is-warning"
          data-testid={`campaign-badge-${plan.id}`}
          style={{ position: 'absolute', top: 12, right: 12 }}
        >
          {t('planCard.campaignBadge', { pct: plan.campaign_discount_pct })}
        </span>
      )}

      <h3 style={{ marginBottom: 4, fontSize: 18, fontWeight: 600 }}>{plan.name}</h3>

      <p style={{ color: 'var(--text-subtle)', fontSize: 13, marginBottom: 16 }}>
        {t('planCard.hotspotLimit', { limit: plan.hotspot_limit })}
      </p>

      <div style={{ marginBottom: 16 }}>
        {/* F2: Waehrung kommt aus dem geladenen Katalog (plan.currency) —
            frueher stand hier fest „€“, obwohl der Katalog z. B. USD lieferte. */}
        <span style={{ fontSize: 28, fontWeight: 700 }}>
          {fmtPrice(perMonth, plan.currency)}
        </span>
        <span style={{ color: 'var(--text-subtle)', fontSize: 13 }}>{t('planCard.perMonth')}</span>
        {duration > 1 && (
          <div style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 4 }}>
            {t('planCard.totalPrice', { total: fmtPrice(total, plan.currency), duration })}
          </div>
        )}
        {plan.campaign_active && (
          <div style={{ fontSize: 12, color: 'var(--c-warning)', marginTop: 2 }}>
            {t('planCard.originalPrice', { price: fmtPrice(plan.price_eur_monthly * duration, plan.currency) })}
          </div>
        )}
      </div>

      {/* Issue BE#24: der Free-Tarif ist nicht abonnierbar — ein Kreditkarten-
          Modal mit „Gesamtpreis 0,00 $“ ist Unsinn (Dennis, 05.09.2026). */}
      {!(plan.id === 'free' || plan.price_eur_monthly === 0) && (
        <button
          className="btn btn-primary"
          style={{ width: '100%' }}
          onClick={() => onSubscribe(plan)}
          data-testid={`subscribe-btn-${plan.id}`}
        >
          {t('planCard.subscribeButton')}
        </button>
      )}
    </div>
  )
}
